import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BRAND_LOGO_API = `${API}/brand/logo`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Redirect to /login on 401 (except from the login call itself).
// Athlete-portal routes redirect to /athlete/login instead.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (
      err.response &&
      err.response.status === 401 &&
      !(err.config?.url || "").endsWith("/auth/login") &&
      !(err.config?.url || "").endsWith("/auth/me") &&
      !(err.config?.url || "").endsWith("/athlete-auth/login") &&
      !(err.config?.url || "").endsWith("/athlete-auth/me") &&
      !(err.config?.url || "").endsWith("/athlete-auth/set-password")
    ) {
      const onAthleteRoute = window.location.pathname.startsWith("/athlete");
      const isAthleteApi =
        (err.config?.url || "").includes("/athlete/me/") ||
        (err.config?.url || "").includes("/athlete-auth/");
      const here = window.location.pathname + window.location.search;
      if (onAthleteRoute || isAthleteApi) {
        if (window.location.pathname !== "/athlete/login") {
          window.location.href = `/athlete/login?next=${encodeURIComponent(here)}`;
        }
      } else if (window.location.pathname !== "/login") {
        window.location.href = `/login?next=${encodeURIComponent(here)}`;
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (password) => api.post("/auth/login", { password }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);
export const me = () => api.get("/auth/me").then((r) => r.data);

// Athletes
export const listAthletes = () => api.get("/athletes").then((r) => r.data);
export const getAthlete = (id) => api.get(`/athletes/${id}`).then((r) => r.data);
export const createAthlete = (body) => api.post("/athletes", body).then((r) => r.data);
export const updateAthlete = (id, body) => api.put(`/athletes/${id}`, body).then((r) => r.data);
export const deleteAthlete = (id) => api.delete(`/athletes/${id}`).then((r) => r.data);

// Tests
export const listTests = (athleteId) =>
  api.get("/tests", { params: athleteId ? { athlete_id: athleteId } : {} }).then((r) => r.data);
export const getTest = (id) => api.get(`/tests/${id}`).then((r) => r.data);
export const createTest = (body) => api.post("/tests", body).then((r) => r.data);
export const updateTest = (id, body) => api.put(`/tests/${id}`, body).then((r) => r.data);
export const deleteTest = (id) => api.delete(`/tests/${id}`).then((r) => r.data);

// Email
export const emailStatus = () => api.get("/email/status").then((r) => r.data);
export const sendReportEmail = (body) =>
  api.post("/email/send-report", body).then((r) => r.data);
export const listEmailSends = (athleteId) =>
  api.get(`/athletes/${athleteId}/email-sends`).then((r) => r.data);

// Coaching — athletes
export const listCoachingAthletes = () =>
  api.get("/coaching/athletes").then((r) => r.data);
export const getCoachingAthlete = (id) =>
  api.get(`/coaching/athletes/${id}`).then((r) => r.data);
export const createCoachingAthlete = (body) =>
  api.post("/coaching/athletes", body).then((r) => r.data);
export const updateCoachingAthlete = (id, body) =>
  api.put(`/coaching/athletes/${id}`, body).then((r) => r.data);
export const deleteCoachingAthlete = (id) =>
  api.delete(`/coaching/athletes/${id}`).then((r) => r.data);

// Coaching — programs
export const listPrograms = (params = {}) =>
  api.get("/coaching/programs", { params }).then((r) => r.data);
export const getProgram = (id) =>
  api.get(`/coaching/programs/${id}`).then((r) => r.data);
export const createProgram = (body) =>
  api.post("/coaching/programs", body).then((r) => r.data);
export const updateProgram = (id, body) =>
  api.put(`/coaching/programs/${id}`, body).then((r) => r.data);
export const deleteProgram = (id) =>
  api.delete(`/coaching/programs/${id}`).then((r) => r.data);
export const addProgramWeek = (id) =>
  api.post(`/coaching/programs/${id}/weeks`).then((r) => r.data);
export const duplicateProgramWeek = (id, weekNumber) =>
  api
    .post(`/coaching/programs/${id}/weeks/${weekNumber}/duplicate`)
    .then((r) => r.data);
export const deleteProgramWeek = (id, weekNumber) =>
  api
    .delete(`/coaching/programs/${id}/weeks/${weekNumber}`)
    .then((r) => r.data);
export const reorderProgramWeeks = (id, order) =>
  api
    .post(`/coaching/programs/${id}/weeks/reorder`, { order })
    .then((r) => r.data);
export const cloneProgram = (id, body = {}) =>
  api.post(`/coaching/programs/${id}/clone`, body).then((r) => r.data);
// Insert a slice of weeks from a source (library) program INTO an existing
// target (athlete's) program. See backend `insert_from_program`.
export const insertFromProgram = (targetProgramId, body) =>
  api
    .post(`/coaching/programs/${targetProgramId}/insert-from`, body)
    .then((r) => r.data);
export const listStrengthExerciseNames = () =>
  api.get("/coaching/strength-exercises/names").then((r) => r.data.names || []);

// Coaching — Strava
export const stravaStatus = () => api.get("/strava/status").then((r) => r.data);
export const athleteStravaStatus = (athleteId) =>
  api
    .get(`/coaching/athletes/${athleteId}/strava/status`)
    .then((r) => r.data);
export const createStravaLinkUrl = (athleteId) =>
  api
    .post(`/coaching/athletes/${athleteId}/strava/link-url`)
    .then((r) => r.data);
export const unlinkStrava = (athleteId) =>
  api
    .delete(`/coaching/athletes/${athleteId}/strava`)
    .then((r) => r.data);
export const syncStravaActivities = (athleteId, days = 30) =>
  api
    .post(`/coaching/athletes/${athleteId}/strava/sync`, null, {
      params: { days },
    })
    .then((r) => r.data);
export const listStravaActivities = (athleteId, params = {}) =>
  api
    .get(`/coaching/athletes/${athleteId}/strava/activities`, { params })
    .then((r) => r.data);
export const sendStravaInvite = (athleteId) =>
  api
    .post(`/coaching/athletes/${athleteId}/strava/invite`)
    .then((r) => r.data);
export const setStravaActivityCountsAsVolume = (athleteId, activityId, countsAsVolume) =>
  api
    .patch(
      `/coaching/athletes/${athleteId}/strava/activities/${activityId}`,
      { counts_as_volume: countsAsVolume },
    )
    .then((r) => r.data);
export const getStravaInviteState = (state) =>
  api.get(`/coaching/strava/invite-state/${state}`).then((r) => r.data);
export const getWeekCompletion = (programId, weekNumber) =>
  api
    .get(`/coaching/programs/${programId}/weeks/${weekNumber}/completion`)
    .then((r) => r.data);


// --- Athlete portal ---
export const inviteAthlete = (athleteId, body) =>
  api.post(`/coaching/athletes/${athleteId}/invite`, body || {}).then((r) => r.data);
export const getInvitePreview = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/invite/preview`).then((r) => r.data);
export const getStudioSettings = () =>
  api.get("/coaching/settings").then((r) => r.data);
export const updateStudioSettings = (body) =>
  api.put("/coaching/settings", body).then((r) => r.data);
export const athleteSetPassword = (token, password) =>
  api.post("/athlete-auth/set-password", { token, password }).then((r) => r.data);
export const athleteLogin = (email, password) =>
  api.post("/athlete-auth/login", { email, password }).then((r) => r.data);
export const athleteLogout = () =>
  api.post("/athlete-auth/logout").then((r) => r.data);
export const athleteMe = () =>
  api.get("/athlete-auth/me").then((r) => r.data);
export const athleteListPrograms = () =>
  api.get("/athlete/me/programs").then((r) => r.data);
export const athleteGetProgram = (programId) =>
  api.get(`/athlete/me/programs/${programId}`).then((r) => r.data);
export const athleteShiftWorkout = (programId, body) =>
  api.post(`/athlete/me/programs/${programId}/shift`, body).then((r) => r.data);
export const coachListWorkoutShifts = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/workout-shifts`).then((r) => r.data);
export const athleteLogWorkout = (body) =>
  api.post("/athlete/me/workout-logs", body).then((r) => r.data);
export const getAthletePendingStravaMatches = () =>
  api.get("/athlete/me/strava/pending-matches").then((r) => r.data);
export const resolveAthleteStravaMatch = (stravaActivityId, choice) =>
  api
    .post(`/athlete/me/strava/activities/${stravaActivityId}/match`, choice || {})
    .then((r) => r.data);
export const getAthleteActivityFeed = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/activity-feed`).then((r) => r.data);
export const getAthleteFourWeekOverview = (athleteId, params = {}) =>
  api.get(`/coaching/athletes/${athleteId}/four-week-overview`, { params }).then((r) => r.data);
export const coachOverrideWorkoutLog = (athleteId, body) =>
  api.put(`/coaching/athletes/${athleteId}/workout-logs`, body).then((r) => r.data);
export const coachDeleteWorkoutLog = (athleteId, programId, weekNumber, dayLabel, workoutId) =>
  api
    .delete(
      `/coaching/athletes/${athleteId}/workout-logs/${programId}/${weekNumber}/${encodeURIComponent(dayLabel)}/${workoutId}`
    )
    .then((r) => r.data);
export const sendAthleteReportCard = (athleteId, body) =>
  api.post(`/coaching/athletes/${athleteId}/report-card/send`, body).then((r) => r.data);
export const getAthleteReportCardLastSent = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/report-card/last-sent`).then((r) => r.data);
export const getAthleteReportCardDraft = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/report-card/draft`).then((r) => r.data);
export const getAthleteReportCardPreviewUrl = (athleteId, params = {}) => {
  const q = new URLSearchParams();
  if (params.headline) q.set("headline", params.headline);
  if (params.went_well) q.set("went_well", params.went_well);
  if (params.focus_next) q.set("focus_next", params.focus_next);
  if (params.notes) q.set("notes", params.notes);
  // Custom report range — Monday ISO of the last week to include, plus
  // how many weeks (1–12) to summarise. Coach defaults pick the previous
  // completed week so a Monday-morning send doesn't bake in this half-
  // empty week. Backend snaps to Monday and clamps future dates.
  if (params.end_week_start) q.set("end_week_start", params.end_week_start);
  if (params.weeks_back != null) q.set("weeks_back", String(params.weeks_back));
  if (params.cache_bust != null) q.set("_k", String(params.cache_bust));
  const qs = q.toString();
  return `${API}/coaching/athletes/${athleteId}/report-card/preview${qs ? `?${qs}` : ""}`;
};
export const getCoachingDashboardToday = () =>
  api.get("/coaching/dashboard/today").then((r) => r.data);
export const getNeedsAttention = () =>
  api.get("/coaching/dashboard/needs-attention").then((r) => r.data);
export const getProgramWorkoutLogs = (programId) =>
  api.get(`/coaching/programs/${programId}/workout-logs`).then((r) => r.data);
export const athleteGetLactate = () =>
  api.get("/athlete/me/lactate").then((r) => r.data);
export const athleteListStepLogs = (startIso, endIso) =>
  api
    .get("/athlete/me/step-logs", { params: { start: startIso, end: endIso } })
    .then((r) => r.data);
export const athleteUpsertStepLog = ({ date, status, actual_steps, target }) =>
  api
    .post("/athlete/me/step-logs", { date, status, actual_steps, target })
    .then((r) => r.data);
export const sendDigestNow = (programId, weekNumber, force = false) =>
  api
    .post(`/coaching/programs/${programId}/send-digest`, null, {
      params: { week_number: weekNumber, force },
    })
    .then((r) => r.data);
export const getDigestPreviewUrl = (programId, weekNumber) =>
  `${API}/coaching/programs/${programId}/digest-preview?week_number=${weekNumber}`;
export const getDailyPreviewUrl = (programId, dateIso) =>
  `${API}/coaching/programs/${programId}/daily-preview${dateIso ? `?date_iso=${dateIso}` : ""}`;
export const getForgotLogPreviewUrl = (athleteId) =>
  `${API}/coaching/athletes/${athleteId}/forgot-log-preview`;
export const getAthleteLastSeen = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/last-seen`).then((r) => r.data);
export const getLastDigest = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/last-digest`).then((r) => r.data);
export const getDigestStatusesBulk = () =>
  api.get("/coaching/digest/athlete-statuses").then((r) => r.data);
export const getPortalStatusesBulk = () =>
  api.get("/coaching/athletes-portal-statuses").then((r) => r.data);
export const getWeekVolumeBulk = () =>
  api.get("/coaching/athletes-week-volume").then((r) => r.data);
export const getWeekGlance = (params) =>
  api.get("/coaching/week-glance", { params }).then((r) => r.data);
export const getNextDigest = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/next-digest`).then((r) => r.data);
export const getCoachingAthleteLactate = (athleteId) =>
  api.get(`/coaching/athletes/${athleteId}/lactate`).then((r) => r.data);
export const getCoachingAthleteLactateSuggestion = (athleteId) =>
  api
    .get(`/coaching/athletes/${athleteId}/lactate-suggestion`)
    .then((r) => r.data);
export const getCoachAthletePreview = (programId) =>
  api
    .get(`/coaching/programs/${programId}/athlete-preview`)
    .then((r) => r.data);

export const athleteGetCalendarInfo = () =>
  api.get(`/athlete/me/calendar-info`).then((r) => r.data);
export const athleteUpdateCalendarSettings = (payload) =>
  api.put(`/athlete/me/calendar-settings`, payload).then((r) => r.data);
export const athleteRotateCalendarToken = () =>
  api.post(`/athlete/me/calendar-rotate-token`).then((r) => r.data);

// --- Athlete strength set logging ----------------------------------------
export const athletePutStrengthSets = (payload) =>
  api.put(`/athlete/me/strength-sets`, payload).then((r) => r.data);
export const athleteGetStrengthSets = (programId, weekNumber, dayLabel, workoutId) =>
  api
    .get(`/athlete/me/strength-sets`, {
      params: {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
      },
    })
    .then((r) => r.data);
export const athleteGetProgress = () => api.get("/athlete/me/progress").then((r) => r.data);
export const coachGetAthleteProgress = (athleteId) => api.get(`/coaching/athletes/${athleteId}/progress`).then((r) => r.data);
export const athleteGetGearHistory = (limit = 12) =>
  api.get("/athlete/me/gear-history", { params: { limit } }).then((r) => r.data.entries || []);
export const athleteGetNutritionHistory = (limit = 8) =>
  api
    .get("/athlete/me/nutrition-history", { params: { limit } })
    .then((r) => r.data.entries || []);

export const athleteAddCustomWorkout = (body) =>
  api.post("/athlete/me/custom-workouts", body).then((r) => r.data);

export const athleteDeleteCustomWorkout = (workoutId) =>
  api.delete(`/athlete/me/custom-workouts/${workoutId}`).then((r) => r.data);

export const coachProgramAthleteExtras = (programId) =>
  api.get(`/coaching/programs/${programId}/athlete-extras`).then((r) => r.data);

export const athleteGetStrengthHistory = (exercise, limit = 5) =>
  api
    .get(`/athlete/me/strength-history`, {
      params: { exercise, limit },
    })
    .then((r) => r.data);

// --- Strength swap suggestions (no auth — static catalogue) ---------------
export const getStrengthSwapSuggestions = (name, limit = 8) =>
  api
    .get(`/strength/swap-suggestions`, { params: { name, limit } })
    .then((r) => r.data);

// --- Workout comments (coach ↔ athlete threaded notes) -------------------
export const athleteListWorkoutComments = (programId, weekNumber, dayLabel, workoutId) =>
  api
    .get("/athlete/me/workout-comments", {
      params: {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
      },
    })
    .then((r) => r.data.comments || []);

export const athletePostWorkoutComment = (payload) =>
  api.post("/athlete/me/workout-comments", payload).then((r) => r.data);

export const athleteUnreadCommentCount = () =>
  api.get("/athlete/me/workout-comments-unread-count").then((r) => r.data);

export const athleteCoachNotifications = () =>
  api.get("/athlete/me/coach-notifications").then((r) => r.data);

export const athleteMarkAllNotificationsRead = () =>
  api.post("/athlete/me/coach-notifications/mark-all-read").then((r) => r.data);

export const coachListWorkoutComments = (athleteId, programId, weekNumber, dayLabel, workoutId) =>
  api
    .get(`/coaching/athletes/${athleteId}/workout-comments`, {
      params: {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
      },
    })
    .then((r) => r.data.comments || []);

export const coachPostWorkoutComment = (athleteId, payload) =>
  api
    .post(`/coaching/athletes/${athleteId}/workout-comments`, payload)
    .then((r) => r.data);

export const coachListConversations = (athleteId) =>
  api
    .get(`/coaching/athletes/${athleteId}/conversations`)
    .then((r) => r.data.threads || []);

export const athleteGetMessages = () =>
  api.get("/athlete/me/messages").then((r) => r.data);

export const athleteSendMessage = (body, photos = [], audioBase64 = null) =>
  api
    .post("/athlete/me/messages", { body, photo_base64s: [].concat(photos || []), audio_base64: audioBase64 })
    .then((r) => r.data);

export const athleteTypingPing = () =>
  api.post("/athlete/me/messages/typing").then((r) => r.data);

export const athleteMessagesUnreadCount = () =>
  api.get("/athlete/me/messages-unread-count").then((r) => r.data);

export const coachListMessageThreads = () =>
  api.get("/coaching/messages").then((r) => r.data.threads || []);

export const coachMessagesUnreadCount = () =>
  api.get("/coaching/messages-unread-count").then((r) => r.data);

export const coachGetMessages = (athleteId) =>
  api.get(`/coaching/messages/${athleteId}`).then((r) => r.data);

export const coachSendMessage = (athleteId, body, photos = [], audioBase64 = null) =>
  api
    .post(`/coaching/messages/${athleteId}`, { body, photo_base64s: [].concat(photos || []), audio_base64: audioBase64 })
    .then((r) => r.data);

export const coachTypingPing = (athleteId) =>
  api.post(`/coaching/messages/${athleteId}/typing`).then((r) => r.data);

export const getCoachPlanningStatus = () =>
  api.get("/coaching/planning-status").then((r) => r.data);

export const coachActivityFeed = (days = 14, limit = 50) =>
  api.get("/coaching/activity-feed", { params: { days, limit } }).then((r) => r.data);

// --- Coach-side: see athlete's strength logs ------------------------------
export const coachGetAthleteStrengthSets = (athleteId, params = {}) =>
  api
    .get(`/coaching/athletes/${athleteId}/strength-sets`, { params })
    .then((r) => r.data);



export const getCoachCalendarDebug = (athleteId) =>
  api
    .get(`/coaching/athletes/${athleteId}/calendar-debug`)
    .then((r) => r.data);




// --- Coach email templates (server-side so they follow the coach across
// browsers/devices instead of being trapped in localStorage). template_type
// is one of "test" | "compare" | "coaching_week". -----------------------
export const getEmailTemplate = (templateType) =>
  api
    .get(`/coach/email-templates/${templateType}`)
    .then((r) => r.data);

export const putEmailTemplate = (templateType, body) =>
  api
    .put(`/coach/email-templates/${templateType}`, body)
    .then((r) => r.data);

export const deleteEmailTemplate = (templateType) =>
  api.delete(`/coach/email-templates/${templateType}`).then((r) => r.data);
